import React, { useEffect, useMemo, useState } from "react";
import {
  recupererProduitsLaitAdmin,
  sauvegarderTypeLait,
} from "../utils/productService";

/**
 * ============================================================
 * AdminMilkTypes.js
 * ============================================================
 *
 * RÔLE
 * ----------------------------------------------------------------------------
 * Page admin dédiée au système "type de lait".
 *
 * Cette page est volontairement séparée de l’admin inventaire :
 * - pas de modification d’AdminDashboard.js
 * - pas de couplage avec les modals produit legacy
 * - rollback simple en cas de besoin
 *
 * RESPONSABILITÉS
 * ----------------------------------------------------------------------------
 * - charger la liste admin produits + enrichissement type_lait / lait_animal
 * - filtrer localement (recherche / catégorie / type de lait / lait animal)
 * - sauvegarder le type de lait et le lait animal produit par produit
 *
 * CONTRAINTES
 * ----------------------------------------------------------------------------
 * - aucune logique pricing ici
 * - aucune logique checkout ici
 * - aucune logique produit cave ici
 * - on passe impérativement par productService.js
 *
 * IMPORTANT ENVIRONNEMENT
 * ----------------------------------------------------------------------------
 * On n’utilise PAS de fetch relatif "/services/...".
 * En environnement front type CodeSandbox, cela peut renvoyer l’index HTML
 * de l’app au lieu du JSON PHP.
 *
 * En passant par productService.js, on réutilise les endpoints absolus déjà
 * centralisés, ce qui garde :
 * - le sandbox stable
 * - la prod cohérente
 * - une seule source de vérité pour les URLs backend
 */

const TYPE_OPTIONS = ["", "lait cru", "pasteurisé", "thermisé"];
const LAIT_ANIMAL_OPTIONS = ["", "vache", "brebis", "chèvre", "mixte"];

/**
 * ============================================================
 * GUIDE — NORMALISATION TYPE DE LAIT
 * ============================================================
 *
 * Sécurise la lecture front :
 * - si type vide / non string => ""
 * - sinon trim simple
 *
 * Le backend reste la source de vérité métier.
 */
const normalizeMilkType = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

/**
 * ============================================================
 * GUIDE — NORMALISATION LAIT ANIMAL
 * ============================================================
 *
 * Sécurise la lecture front :
 * - vache
 * - brebis
 * - chèvre
 * - mixte
 */
const normalizeLaitAnimal = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
};

export default function AdminMilkTypes() {
  /**
   * ============================================================
   * GUIDE — ÉTAT LOCAL
   * ============================================================
   *
   * items      : données affichées
   * loading    : chargement initial / refresh
   * savingId   : identifie la ligne en cours de sauvegarde
   * search     : recherche texte (nom ou id)
   * category   : filtre catégorie
   * milkType   : filtre type de lait
   * laitAnimal : filtre lait animal
   * message    : feedback simple admin
   */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [milkType, setMilkType] = useState("");
  const [laitAnimal, setLaitAnimal] = useState("");
  const [message, setMessage] = useState("");

  /**
   * ============================================================
   * GUIDE — CHARGEMENT INITIAL
   * ============================================================
   */
  useEffect(() => {
    fetchItems();
  }, []);

  /**
   * ============================================================
   * GUIDE — CHARGEMENT LISTE ADMIN
   * ============================================================
   *
   * On passe par productService pour :
   * - réutiliser les endpoints centralisés
   * - éviter les URLs relatives fragiles
   * - garder le comportement homogène avec le reste du projet
   */
  const fetchItems = async () => {
    setLoading(true);
    setMessage("");

    try {
      const nextItems = await recupererProduitsLaitAdmin();
      setItems(Array.isArray(nextItems) ? nextItems : []);
    } catch (err) {
      setMessage(err?.message || "Erreur chargement");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * ============================================================
   * GUIDE — FILTRES DISPONIBLES
   * ============================================================
   *
   * Les catégories sont reconstruites depuis les données chargées.
   * On garde un tri simple pour lecture admin plus stable.
   */
  const categories = useMemo(() => {
    return [...new Set(items.map((i) => i.category).filter(Boolean))].sort();
  }, [items]);

  /**
   * ============================================================
   * GUIDE — FILTRAGE LOCAL
   * ============================================================
   *
   * Filtrage purement UI :
   * - aucun impact backend
   * - aucun impact sur la source de vérité
   */
  const filteredItems = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchSearch =
        !searchValue ||
        (item.name || "").toLowerCase().includes(searchValue) ||
        String(item.id).includes(searchValue);

      const matchCategory = !category || item.category === category;

      const matchMilkType =
        !milkType || normalizeMilkType(item.type_lait) === milkType;

      const matchLaitAnimal =
        !laitAnimal ||
        normalizeLaitAnimal(item.lait_animal) === normalizeLaitAnimal(laitAnimal);

      return matchSearch && matchCategory && matchMilkType && matchLaitAnimal;
    });
  }, [items, search, category, milkType, laitAnimal]);

  /**
   * ============================================================
   * GUIDE — MODIFICATION LOCALE TYPE DE LAIT
   * ============================================================
   *
   * On met à jour uniquement l’état local pour garder une UI réactive.
   * La vraie persistance se fait au clic sur "Sauvegarder".
   */
  const handleTypeChange = (id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, type_lait: normalizeMilkType(value) } : item
      )
    );
  };

  /**
   * ============================================================
   * GUIDE — MODIFICATION LOCALE LAIT ANIMAL
   * ============================================================
   *
   * On met à jour uniquement l’état local pour garder une UI réactive.
   * La vraie persistance se fait au clic sur "Sauvegarder".
   */
  const handleLaitAnimalChange = (id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, lait_animal: normalizeLaitAnimal(value) }
          : item
      )
    );
  };

  /**
   * ============================================================
   * GUIDE — SAUVEGARDE
   * ============================================================
   *
   * Le backend gère :
   * - l’upsert si type_lait / lait_animal est renseigné
   * - la suppression / reset selon logique backend si valeurs vides
   *
   * On passe impérativement par le helper centralisé :
   * sauvegarderTypeLait(produitId, typeLait, laitAnimal)
   */
  const saveMilkType = async (item) => {
    setSavingId(item.id);
    setMessage("");

    try {
      await sauvegarderTypeLait(
        item.id,
        item.type_lait || "",
        item.lait_animal || ""
      );

      setMessage(`Produit #${item.id} sauvegardé.`);
    } catch (err) {
      setMessage(err?.message || "Erreur sauvegarde");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>Gestion type de lait</h1>

      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}
      >
        <input
          type="text"
          placeholder="Recherche nom ou ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Toutes catégories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <select value={milkType} onChange={(e) => setMilkType(e.target.value)}>
          <option value="">Tous types lait</option>
          <option value="lait cru">lait cru</option>
          <option value="pasteurisé">pasteurisé</option>
          <option value="thermisé">thermisé</option>
        </select>

        <select
          value={laitAnimal}
          onChange={(e) => setLaitAnimal(e.target.value)}
        >
          <option value="">Tous laits animaux</option>
          <option value="vache">vache</option>
          <option value="brebis">brebis</option>
          <option value="chèvre">chèvre</option>
          <option value="mixte">mixte</option>
        </select>

        <button onClick={fetchItems}>Rafraîchir</button>
      </div>

      {message && (
        <div style={{ marginBottom: 12, padding: 10, background: "#f5f5f5" }}>
          {message}
        </div>
      )}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th align="left">ID</th>
                <th align="left">Nom</th>
                <th align="left">Catégorie</th>
                <th align="left">Prix</th>
                <th align="left">Stock</th>
                <th align="left">Online</th>
                <th align="left">Type de lait</th>
                <th align="left">Lait animal</th>
                <th align="left">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id} style={{ borderTop: "1px solid #ddd" }}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.category}</td>
                  <td>{item.price}</td>
                  <td>{item.stock}</td>
                  <td>{Number(item.isOnline) === 1 ? "Oui" : "Non"}</td>

                  <td>
                    <select
                      value={normalizeMilkType(item.type_lait)}
                      onChange={(e) =>
                        handleTypeChange(item.id, e.target.value)
                      }
                    >
                      <option value="">— aucun —</option>
                      {TYPE_OPTIONS.filter(Boolean).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      value={normalizeLaitAnimal(item.lait_animal)}
                      onChange={(e) =>
                        handleLaitAnimalChange(item.id, e.target.value)
                      }
                    >
                      <option value="">— aucun —</option>
                      {LAIT_ANIMAL_OPTIONS.filter(Boolean).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <button
                      onClick={() => saveMilkType(item)}
                      disabled={savingId === item.id}
                    >
                      {savingId === item.id ? "Sauvegarde..." : "Sauvegarder"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!filteredItems.length && <p>Aucun produit trouvé.</p>}
        </div>
      )}
    </div>
  );
}